import React from 'react';
import { useBattle } from '../context';

/** カード情報パネルの位置 */
export type CardInfoPosition = 'left' | 'right' | 'hidden';

/** TopBar Props */
interface TopBarProps {
  /** サイドパネル表示コールバック（モバイルのみ） */
  onShowSidePanel?: () => void;
  /** フェイズ名配列 */
  PHASES: string[];
  /** カード情報パネルの位置（デスクトップのみ） */
  cardInfoPosition?: CardInfoPosition;
  /** カード情報パネル位置変更コールバック（デスクトップのみ） */
  onCardInfoPositionChange?: (position: CardInfoPosition) => void;
  /** ヘルプボタンクリック時のコールバック */
  onHelpClick?: () => void;
}

/**
 * TopBar コンポーネント
 *
 * デュエル画面上部のゲーム状況表示バー
 * - ターン情報（P1/P2、ターン数）
 * - フェイズ表示
 * - 情報ボタン（モバイルのみ）
 */
const TopBar: React.FC<TopBarProps> = ({
  onShowSidePanel,
  PHASES,
  cardInfoPosition = 'left',
  onCardInfoPositionChange,
  onHelpClick,
}) => {
  const { currentPlayer, turn, phase, isMobile } = useBattle();

  // サイクル切り替え: left → right → hidden → left
  const cycleCardInfoPosition = () => {
    if (!onCardInfoPositionChange) return;
    const nextPosition: CardInfoPosition =
      cardInfoPosition === 'left' ? 'right' :
      cardInfoPosition === 'right' ? 'hidden' : 'left';
    onCardInfoPositionChange(nextPosition);
  };

  // 位置に応じたアイコン
  const getPositionIcon = () => {
    switch (cardInfoPosition) {
      case 'left': return '📋◀';
      case 'right': return '▶📋';
      case 'hidden': return '📋✕';
    }
  };

  // プレイヤーカラー
  const playerColor = currentPlayer === 1 ? '#4da6ff' : '#ff6b6b';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isMobile ? '6px' : '12px',
        padding: isMobile ? '6px 12px' : '8px 16px',
        background: 'linear-gradient(90deg, rgba(20,20,50,0.95) 0%, rgba(40,30,60,0.95) 50%, rgba(20,20,50,0.95) 100%)',
        borderBottom: '1px solid rgba(107,76,230,0.3)',
        flexShrink: 0,
        minHeight: '44px',
      }}
    >
      {/* ターン情報 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        {/* 現在プレイヤー */}
        <span
          style={{
            background: playerColor,
            padding: isMobile ? '2px 8px' : '4px 10px',
            borderRadius: '4px',
            fontWeight: 'bold',
            fontSize: isMobile ? '11px' : '13px',
            color: '#fff',
          }}
        >
          P{currentPlayer}
        </span>
        {/* ターン数 */}
        <span
          style={{
            fontSize: isMobile ? '11px' : '13px',
            color: '#aaa',
          }}
        >
          T{turn}
        </span>
      </div>

      {/* フェイズ表示 */}
      <div
        style={{
          display: 'flex',
          gap: isMobile ? '2px' : '4px',
        }}
      >
        {PHASES.map((p, i) => (
          <div
            key={i}
            style={{
              padding: isMobile ? '4px 6px' : '6px 12px',
              borderRadius: '4px',
              fontSize: isMobile ? '10px' : '11px',
              fontWeight: 'bold',
              background: phase === i
                ? 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)'
                : 'rgba(40,40,60,0.6)',
              color: phase === i ? '#fff' : '#666',
              boxShadow: phase === i ? '0 0 10px rgba(107,76,230,0.4)' : 'none',
              transition: 'all 0.2s ease',
            }}
          >
            {isMobile ? p.slice(0, 2) : p}
          </div>
        ))}
      </div>

      {/* 情報ボタン（モバイル: モーダル表示、デスクトップ: 位置サイクル切り替え） */}
      {isMobile ? (
        onShowSidePanel && (
          <button
            onClick={onShowSidePanel}
            style={{
              padding: '4px 8px',
              background: 'rgba(107,76,230,0.3)',
              border: '1px solid rgba(107,76,230,0.5)',
              borderRadius: '4px',
              color: '#a78bfa',
              fontSize: '11px',
              cursor: 'pointer',
            }}
          >
            📋
          </button>
        )
      ) : (
        onCardInfoPositionChange && (
          <button
            onClick={cycleCardInfoPosition}
            title={`カード情報: ${cardInfoPosition === 'left' ? '左' : cardInfoPosition === 'right' ? '右' : '非表示'}`}
            style={{
              padding: '4px 8px',
              background: 'rgba(107,76,230,0.3)',
              border: '1px solid rgba(107,76,230,0.5)',
              borderRadius: '4px',
              color: '#a78bfa',
              fontSize: '11px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            {getPositionIcon()}
          </button>
        )
      )}

      {/* ヘルプボタン */}
      {onHelpClick && (
        <button
          onClick={onHelpClick}
          title="遊び方ガイド"
          style={{
            padding: '4px 8px',
            background: 'rgba(107, 158, 255, 0.2)',
            border: '1px solid rgba(107, 158, 255, 0.4)',
            borderRadius: '4px',
            color: '#a0c0ff',
            fontSize: '11px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          ❓
        </button>
      )}
    </div>
  );
};

export default TopBar;
